import { Box } from '@mui/material';
import { GridCellParams, GridColDef } from '@mui/x-data-grid';
import React, { FC, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import {
    formatDateTimeString,
    IOrganisation,
    ISupportType,
    Page,
    SupportCaseSupportees,
    Table,
    useQueryParamsContext,
    useSelectedOrganisation,
} from '../../../shared';
import { RegistrationsFilter } from '../../components/registrations-filter/registrations-filter.component';
import { useRegistrations } from '../../hooks/use-registrations.hook';
import { IRegistrationReport } from '../../models/registration-report.model';
import { SearchInput } from '../../../shared/components/search/search.component';
import { mapEndDateToApi, mapStartDateToApi } from '../../../shared/mappers/date-string.mapper';

export const ReportRegistrations: FC = () => {
    const { t } = useTranslation();
    const navigate = useNavigate();

    const {
        queryParams: { page, pageSize, sort, filter, search },
        debouncedQueryParams,
        setPage,
        setPageSize,
        setSort,
        setFilter,
        setSearch,
    } = useQueryParamsContext('report-registrations', {});

    const { data: registrations, isPending } = useRegistrations(
        {
            page,
            pageSize,
            startDate: mapStartDateToApi(filter.startDate),
            endDate: mapEndDateToApi(filter.endDate),
            organisationIds: filter.organisations,
            search: debouncedQueryParams.search.replaceAll(/  +/g, ' ').trim(),
        },
        !!filter.organisations,
    );
    const { organisations: organisationOptions } = useSelectedOrganisation(); //organizations that I have access to

    useEffect(() => {
        if (!filter?.organisations && organisationOptions) {
            setFilter({
                startDate: new Date().toString(),
                endDate: new Date().toString(),
                organisations: organisationOptions?.map(({ id }) => id),
            });
        }
    }, [filter, setFilter, organisationOptions]);

    const columns: GridColDef[] = [
        {
            field: 'dateOfRegistration',
            headerName: t('date'),
            minWidth: 200,
            flex: 1,
            sortable: false,
            valueFormatter: (value) => formatDateTimeString(value),
        },
        {
            field: 'supportType',
            headerName: t('supportType'),
            sortable: false,
            minWidth: 200,
            flex: 1,
            valueFormatter: (value: ISupportType) => value?.name,
        },
        {
            field: 'cards',
            headerName: t('cards'),
            sortable: false,
            minWidth: 200,
            flex: 1,
            valueFormatter: (_, { supportCase }: IRegistrationReport) =>
                (supportCase?.cards || []).length > 1 ? t('multipleCards') : (supportCase?.cards?.[0]?.code ?? ''),
        },
        {
            field: 'supportCase',
            headerName: t('supportCase'),
            sortable: false,
            minWidth: 300,
            flex: 1,
            renderCell: ({ row: { supportCase } }: GridCellParams<IRegistrationReport>) =>
                supportCase ? (
                    <SupportCaseSupportees supportCase={supportCase} />
                ) : (
                    <Box sx={{ fontStyle: 'italic' }}>{t('unknown')}</Box>
                ),
        },
        {
            field: 'organisation',
            headerName: t('organisation'),
            sortable: false,
            minWidth: 200,
            flex: 1,
            valueFormatter: (value: IOrganisation) => value.name,
        },
    ];

    return (
        <Page
            title={t('report.registrations')}
            filter={<RegistrationsFilter filter={filter} onChange={setFilter} />}
            loading={isPending}
            actions={<SearchInput value={search} setValue={setSearch} />}
        >
            <Table
                rows={registrations?.data || []}
                columns={columns}
                rowCount={registrations?.pagination?.size || 0}
                page={page}
                pageSize={pageSize}
                setPage={setPage}
                setPageSize={setPageSize}
                loading={isPending}
                sortModel={sort}
                onSortModelChange={setSort}
                onRowClick={({ row: registration }) =>
                    registration.supportCase && navigate(`/support/support-cases/${registration.supportCase.id}/detail`)
                }
            />
        </Page>
    );
};
